/**
 * Search Card Style Titles (repo root `Search Card Style Titles.md`): the ONE rule
 * for what a style's search or collection card shows instead of its full product
 * title. Pure; the DOM adapters live in `dom.ts`.
 *
 * Scanner titles every product `<style part> (<colour name>)`. A style's
 * representative card stands for every colour, so it shows the style part only:
 * unless that would make it read the same as another card on screen, in which
 * case it keeps its colour.
 */

/**
 * ST-2: the title without its final bracketed group, counted bracket-aware.
 *
 * Bracket-aware because 412 live titles carry a bracket in the style name
 * ("Burnside Quilted Puffer Jacket (Womens) (Navy)") and four colour names carry
 * one ("Light Blue (Old)"). A title that does not end in `)`, never balances, or
 * would be left empty is returned unchanged.
 */
export function styleCardTitle(title: string): string {
  const trimmed = title.replace(/\s+$/, "");
  if (!trimmed.endsWith(")")) return title;
  let depth = 0;
  for (let i = trimmed.length - 1; i >= 0; i--) {
    const ch = trimmed[i];
    if (ch === ")") {
      depth++;
    } else if (ch === "(") {
      depth--;
      if (depth === 0) {
        const head = trimmed.slice(0, i).replace(/\s+$/, "");
        return head.length > 0 ? head : title;
      }
    }
  }
  return title;
}

/**
 * The key two card titles are compared by: lower case, ® ™ © dropped, whitespace
 * collapsed. "Hammer Maxweight" and "Hammer® MaxWeight" are the same name to a
 * customer reading the grid.
 */
export function cardTitleKey(title: string): string {
  return title
    .replace(/[®™©]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** One card on a surface (a dropdown, a results page, a collection grid). */
export interface CardTitleInput {
  /** The product's full title, colour included. */
  fullTitle: string;
  /** ST-1a/b: whether this card may lose its colour at all. */
  eligible: boolean;
}

/**
 * ST-1c: the title to show on each card of ONE surface, in input order.
 *
 * Every eligible card is shortened, then any shortened card whose key matches
 * another card's (shortened or untouched) shows its full title instead. Two
 * products still tagged as one style's primary, two styles sharing a name, or a
 * title that already equals a sibling's shortened one therefore never show as
 * identical cards.
 */
export function cardTitlesFor(cards: readonly CardTitleInput[]): string[] {
  const proposed = cards.map((card) => (card.eligible ? styleCardTitle(card.fullTitle) : card.fullTitle));
  const counts = new Map<string, number>();
  for (const title of proposed) {
    const key = cardTitleKey(title);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return cards.map((card, index) => {
    const title = proposed[index];
    if (title === card.fullTitle) return title;
    return (counts.get(cardTitleKey(title)) ?? 0) > 1 ? card.fullTitle : title;
  });
}
